import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from "react-router-dom";

import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import topApi from '../../components/api/ToPApi';
import { Stock, StocksResponse } from '../../models/interfaces';
import { StocksContext } from '../../context/stock/StockContext';
import { StockMovementDialog, StockMovementFields } from './StockMovementDialog';


const PAGE_SIZE = 15;

const statusTabs = [
    { key: 'low_stock', label: 'Stock bajo', badgeClass: 'bg-warning-subtle text-warning-emphasis' },
    { key: 'out_of_stock', label: 'Agotado', badgeClass: 'bg-danger-subtle text-danger-emphasis' },
];

export const LowStockPage = () => {
    const [buscar, setBuscar] = useState('');
    const [status, setStatus] = useState<string>('low_stock');
    const [pageActual, setPageActual] = useState<number>(1);

    const [items, setItems] = useState<Stock[]>([]);
    const [totalPage, setTotalPage] = useState<number>(0);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [counts, setCounts] = useState({ lowStock: 0, outOfStock: 0 });
    const [isLoading, setIsLoading] = useState(false);

    // Fila en modo "agregar stock"
    const [addingId, setAddingId] = useState<number | null>(null);
    const [inlineValue, setInlineValue] = useState<string>('');
    const [inlineSaving, setInlineSaving] = useState(false);
    const [error, setError] = useState('');

    const { addStock } = useContext(StocksContext);
    const navigate = useNavigate();

    const [entryDialog, setEntryDialog] = useState<{ stock: Stock; quantity: number } | null>(null);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchItems = async (page: number) => {
        setIsLoading(true);
        try {
            const params: any = { page, pageSize: PAGE_SIZE, status };
            if (buscar) params.search = buscar;
            const resp = await topApi.get<StocksResponse>('/stock/getStocks', { params });
            setItems(resp.data.stocks ?? []);
            setTotalPage(resp.data.totalPages ?? 1);
            setTotalRecords(resp.data.totalRecords ?? 0);
            setCounts({
                lowStock: resp.data.stats?.lowStock ?? 0,
                outOfStock: resp.data.stats?.outOfStock ?? 0,
            });
        } catch (e) {
            setItems([]);
            setTotalPage(0);
            setTotalRecords(0);
        } finally {
            setIsLoading(false);
        }
    }

    // Recarga cuando cambia la página o el estado
    useEffect(() => {
        fetchItems(pageActual);
    }, [pageActual, status])

    // Búsqueda con debounce
    const firstRender = useRef(true);
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (pageActual !== 1) {
                setPageActual(1);
            } else {
                fetchItems(1);
            }
        }, 400);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [buscar])

    const startAdd = (stock: Stock) => {
        setAddingId(stock.id);
        setInlineValue('');
    }

    const cancelInline = () => {
        setAddingId(null);
        setInlineValue('');
    }

    const saveAdd = async (stock: Stock, value: number, fields: StockMovementFields) => {
        setInlineSaving(true);
        const resp = await addStock({ id: stock.id, addStock: value, ...fields });
        setInlineSaving(false);
        if (resp !== 'success') {
            setError(resp);
        } else {
            setEntryDialog(null);
            cancelInline();
            fetchItems(pageActual);
        }
    }

    const confirmAdd = async (stock: Stock) => {
        const value = parseFloat(inlineValue);
        if (isNaN(value) || value <= 0) {
            setError('Ingrese una cantidad válida');
            return;
        }
        // El ingreso entra al costo promedio y lleva motivo (las compras se registran en Compras)
        setEntryDialog({ stock, quantity: value });
    }

    const activeTab = statusTabs.find(t => t.key === status)!;
    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const removeLocalError = () => setError('');

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Alertas de stock</h3>
                    <p className="text-muted mb-0">Productos que están en o por debajo de su cantidad mínima.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/stocks')}>
                        <i className="bi bi-arrow-left me-1" /> Volver al inventario
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    <ul className="nav nav-tabs mb-3">
                        {statusTabs.map(tab => (
                            <li className="nav-item" key={tab.key}>
                                <button
                                    className={`nav-link ${status === tab.key ? 'active' : ''}`}
                                    onClick={() => { setStatus(tab.key); setPageActual(1); cancelInline(); }}
                                >
                                    {tab.label} ({tab.key === 'low_stock' ? counts.lowStock : counts.outOfStock})
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    type="text"
                                    placeholder="Buscar por nombre o código..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Código</th>
                                    <th>Nombre</th>
                                    <th>Categoría</th>
                                    <th className="text-end">Stock actual</th>
                                    <th className="text-end">Mínimo</th>
                                    <th className="text-end">Faltante</th>
                                    <th>Unidad</th>
                                    <th>Proveedor</th>
                                    <th>Estado</th>
                                    <th style={{ textAlign: 'center', minWidth: 190 }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={10} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : items.length > 0 ? (
                                    items.map(item => {
                                        const missing = Math.max(0, Number(item.minimunStock) - Number(item.stock));
                                        const isAddingRow = addingId === item.id;
                                        return (
                                            <tr key={item.id}>
                                                <td className="text-muted">{item.code}</td>
                                                <td className="fw-medium">{item.name}</td>
                                                <td>{item.category?.name ?? '-'}</td>
                                                <td className="text-end fw-semibold">{item.stock}</td>
                                                <td className="text-end">{item.minimunStock}</td>
                                                <td className="text-end text-danger">{missing > 0 ? missing : '-'}</td>
                                                <td>{item.unit}</td>
                                                <td>{item.supplier?.name ?? '-'}</td>
                                                <td>
                                                    <span className={`badge rounded-pill ${activeTab.badgeClass}`}>{activeTab.label}</span>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {isAddingRow ? (
                                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                style={{ width: '100px' }}
                                                                autoFocus
                                                                placeholder="Cantidad"
                                                                value={inlineValue}
                                                                onChange={(e) => setInlineValue(e.target.value)}
                                                                disabled={inlineSaving}
                                                            />
                                                            <button type="button" className="btn btn-sm btn-success" disabled={inlineSaving} onClick={() => confirmAdd(item)}>
                                                                <i className="bi bi-check-lg" />
                                                            </button>
                                                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={inlineSaving} onClick={cancelInline}>
                                                                <i className="bi bi-x-lg" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                title="Agregar cantidad"
                                                                onClick={() => startAdd(item)}
                                                            >
                                                                <i className="bi bi-plus-lg" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary ms-1"
                                                                title="Editar producto"
                                                                onClick={() => navigate('/stock', { state: { stock: item } })}
                                                            >
                                                                <i className="bi bi-pencil" />
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr><td colSpan={10} className='text-center py-4 text-muted'>
                                        {status === 'low_stock' ? 'No hay productos con stock bajo.' : 'No hay productos agotados.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <span className="text-muted small">
                            {totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} productos` : ''}
                        </span>
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {
                entryDialog && (
                    <StockMovementDialog
                        mode="add"
                        productName={entryDialog.stock.name}
                        quantity={entryDialog.quantity}
                        averageCost={entryDialog.stock.averageCost === null || entryDialog.stock.averageCost === undefined ? null : Number(entryDialog.stock.averageCost)}
                        saving={inlineSaving}
                        onClose={() => setEntryDialog(null)}
                        onConfirm={(fields) => saveAdd(entryDialog.stock, entryDialog.quantity, fields)}
                    />
                )
            }

            {
                error.length > 0 && (
                    <Message
                        type="error"
                        message={error}
                        closeDialog={removeLocalError}
                        handleOk={removeLocalError}
                        backgroundColor="rgba(0,0,0,0.4)"
                    />
                )
            }
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
};
